import {
  AppBar,
  Box,
  Slide,
  Toolbar,
  useMediaQuery,
  useScrollTrigger,
  useTheme,
} from '@mui/material';
import * as React from 'react';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { GlobalNav } from './GlobalNav/GlobalNav';
import type { GlobalNavProps } from './GlobalNav/GlobalNav.types';
import { LocalLinks, type LocalLinksProps } from './LocalLinks/LocalLinks';
import { LocalNav, type LocalNavProps } from './LocalNav/LocalNav';
import { MobileNav } from './MobileNav/MobileNav';
import {
  ADMIN_TOOLBAR_HEIGHT_PX,
  DISCLAIMER_BANNER_HEIGHT,
  GLOBAL_NAV_HEIGHT,
  LOCAL_LINKS_HEIGHT,
  LOCAL_NAV_HEIGHT,
  PINNED_HEADER_FULL_HEIGHT_CSS_VAR,
  PINNED_HEADER_HEIGHT_CSS_VAR,
} from './styles';
import { useRect } from './useRect';

export interface HeaderGroupProps {
  globalNavProps: GlobalNavProps;
  localNavProps: LocalNavProps;
  localLinksProps: LocalLinksProps;
  localToolbarComponent?: React.ReactNode;
  belowToolbarSlot?: React.ReactNode;
  mobileBelowToolbarSlot?: React.ReactNode;
  topBanner?: React.ReactNode;
}

export const HeaderGroup = ({
  globalNavProps,
  localNavProps,
  localLinksProps,
  localToolbarComponent,
  belowToolbarSlot,
  mobileBelowToolbarSlot,
  topBanner,
}: HeaderGroupProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const scrollTrigger = useScrollTrigger();
  const isAppBarSlideVisible = isMobile ? !scrollTrigger : true;
  const isDesktopNavCollapsed = !isMobile && scrollTrigger;
  const ref = useRef<HTMLDivElement | null>(null);
  const rect = useRect(ref);
  const slideTimeout = 400;

  const syncPinnedHeaderHeightCssVariable = useCallback(() => {
    const appBarElement = ref.current;
    if (!appBarElement) return;

    const pinnedHeaderHeightPx = Math.ceil(appBarElement.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty(
      PINNED_HEADER_HEIGHT_CSS_VAR,
      `${pinnedHeaderHeightPx}px`,
    );
    document.documentElement.style.setProperty(
      PINNED_HEADER_FULL_HEIGHT_CSS_VAR,
      `${appBarElement.offsetHeight}px`,
    );
  }, []);

  useLayoutEffect(() => {
    syncPinnedHeaderHeightCssVariable();

    window.addEventListener('scroll', syncPinnedHeaderHeightCssVariable, { passive: true });
    window.addEventListener('resize', syncPinnedHeaderHeightCssVariable);

    return () => {
      window.removeEventListener('scroll', syncPinnedHeaderHeightCssVariable);
      window.removeEventListener('resize', syncPinnedHeaderHeightCssVariable);
      document.documentElement.style.removeProperty(PINNED_HEADER_HEIGHT_CSS_VAR);
      document.documentElement.style.removeProperty(PINNED_HEADER_FULL_HEIGHT_CSS_VAR);
    };
  }, [syncPinnedHeaderHeightCssVariable, isAppBarSlideVisible, rect.bottom, rect.height]);

  const optionalDisclaimerSpacerPx = topBanner ? DISCLAIMER_BANNER_HEIGHT : 0;
  const optionalAdminToolbarSpacerPx = localToolbarComponent ? ADMIN_TOOLBAR_HEIGHT_PX : 0;

  const spacerFallbackMdPx =
    GLOBAL_NAV_HEIGHT +
    LOCAL_NAV_HEIGHT +
    LOCAL_LINKS_HEIGHT +
    optionalDisclaimerSpacerPx +
    optionalAdminToolbarSpacerPx;

  return (
    <Box data-component="HeaderGroup">
      <Slide appear={false} direction="down" in={isAppBarSlideVisible} timeout={slideTimeout}>
        <AppBar
          sx={{
            transform: isDesktopNavCollapsed
              ? `translateY(-${GLOBAL_NAV_HEIGHT + LOCAL_NAV_HEIGHT + (topBanner ? DISCLAIMER_BANNER_HEIGHT : 0)}px) !important`
              : 'none',
            transition: 'transform 0.4s ease',
            visibility: 'visible !important',
          }}
          ref={ref}
          elevation={0}
        >
          {topBanner}
          <Toolbar
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
            }}
            disableGutters
          >
            <Box
              sx={{
                width: '100%',
                transition: `visibility 0s ${isDesktopNavCollapsed ? slideTimeout : 0}ms`,
              }}
            >
              <GlobalNav {...globalNavProps} />
              <LocalNav {...localNavProps} />
            </Box>
            {localLinksProps.linkData.length > 0 && <LocalLinks {...localLinksProps} />}
          </Toolbar>
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              width: '100%',
              flexShrink: 0,
            }}
          >
            <MobileNav
              activeLink={globalNavProps.activeLink}
              localLinksData={localLinksProps.linkData}
              searchPlaceholder={localNavProps.searchPlaceholder}
              onSearch={localNavProps.onSearch}
              linkComponent={globalNavProps.linkComponent}
              showMyOrderLink={localNavProps.showMyOrderLink}
              myOrderHref={localNavProps.myOrderHref}
              localNavProps={localNavProps}
            />
          </Box>
          {localToolbarComponent}
          {belowToolbarSlot}
          {mobileBelowToolbarSlot && (
            <Box
              sx={{
                display: { xs: 'block', md: 'none' },
                width: '100%',
                flexShrink: 0,
              }}
            >
              {mobileBelowToolbarSlot}
            </Box>
          )}
        </AppBar>
      </Slide>
      <Box
        sx={{
          minHeight: rect.height
            ? `${rect.height}px !important`
            : {
                xs: `calc(4.125rem + ${optionalDisclaimerSpacerPx}px + ${optionalAdminToolbarSpacerPx}px)`,
                md: `${spacerFallbackMdPx}px`,
              },
        }}
      />
    </Box>
  );
};
